import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { getLocalizedValue } from '@card-software/nexus-utilities';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { Business } from '../models/business';
import { TaxAuthorizationProxyService } from '../proxies/tax-authorization.proxy.service';

/**
 * The Business the current site operates under — "us" rather than a counterparty (#4791).
 *
 * <p>Holder of every fiscal range the site issues under its own name, so it decides two things in the UI:
 * what the Tax Authorizations page is scoped to, and whether a business opened in Business Details is our
 * own company (in which case its ranges belong on that page, not on the provider tab).</p>
 *
 * <p>Deliberately asks the API rather than inferring it from the InternalCompany business type: the API
 * resolves the same question when it binds a range's holder, and a screen that disagrees with the server
 * about who "we" are would offer edits the server then rejects. Cached — the answer only changes when the
 * selected site does, and a full reload follows that.</p>
 */
@Injectable({ providedIn: 'root' })
export class SiteBusinessService {
  private readonly proxy = inject(TaxAuthorizationProxyService);
  private readonly translateService = inject(TranslateService);
  private cached?: Observable<Business | null>;

  /**
   * Why the site has no own business, when the API could say. Resolution fails for more than one reason —
   * nothing marked internal, or several things marked internal and no company link to break the tie — and
   * the server's message names the actual culprits, so a screen showing a generic "not configured" instead
   * would send someone looking for the wrong problem. Null when resolution succeeded.
   */
  readonly resolutionError = signal<string | null>(null);

  /** The site's own Business, or null when the site has none configured or resolution failed. */
  public getSiteBusiness(): Observable<Business | null> {
    this.cached ??= this.proxy.getSiteBusiness().pipe(
      map((response) => {
        this.resolutionError.set(null);
        return response.data ?? null;
      }),
      catchError((err) => {
        this.resolutionError.set(getLocalizedValue(this.translateService, err?.error?.errorMessage) || null);
        return of(null);
      }),
      shareReplay(1),
    );
    return this.cached;
  }

  /** True when the given business is the one this site operates under. False for an unknown id. */
  public isSiteBusiness(businessId: string | null | undefined): Observable<boolean> {
    if (!businessId) return of(false);
    return this.getSiteBusiness().pipe(map((business) => !!business?.id && business.id === businessId));
  }
}
